import { jsx, jsxs } from "react/jsx-runtime";
import { css } from "@/styled-system/css";
import { Box, Text, List, Anchor } from "@zillow/constellation-10";
import { yieldCallback } from "@zillow/yield-callback";
import React from "react";
import { GROUP_WIDTH_MAP } from "../../mixins/components/GroupMixin.esm.js";
import { getCmsInfoForPage } from "../../utils/clickstream/getCmsInfoForPage.esm.js";
import { usePageProps } from "../PagePropsProvider/PagePropsProvider.esm.js";
import { sendEvent } from "./B2BMainNav.utils.esm.js";
const columnsNavRootClassName = css({
  paddingInlineStart: "0",
  xl: {
    backgroundColor: "bg.neutral",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderTopColor: "border.muted",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: "border.muted",
    borderLeftWidth: "0",
    borderRightWidth: "0",
    // Full-bleed mega menu: square corners (Menu.Content radius is for compact menus only)
    borderBottomLeftRadius: "{radii.zero}",
    borderBottomRightRadius: "{radii.zero}",
    boxShadow: "lg",
    position: "absolute",
    top: "100%",
    left: "50%",
    right: "auto",
    width: "100vw",
    maxWidth: "100vw",
    transform: "translateX(-50%)",
    zIndex: "dropdown",
    overflow: "hidden"
  },
  xlDown: {
    backgroundColor: "gray.50",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderTopColor: "border.muted",
    borderRadius: "{radii.zero}",
    paddingBlock: "tighter",
    paddingInline: "0",
    position: "relative",
    width: "100%",
    overflow: "hidden"
  }
});
const columnsNavInnerClassName = css({
  display: "flex",
  padding: "loose",
  gap: "looser",
  xlDown: {
    flexDirection: "column",
    gap: "loose",
    padding: "0",
    alignItems: "stretch"
  }
});
const ColumnsNav = ({
  columns,
  transparent,
  isVisible,
  onMouseEnter,
  onMouseLeave
}) => {
  const pageProps = usePageProps();
  const cmsInfo = getCmsInfoForPage(pageProps);
  const handleLinkClick = (label, url) => {
    sendEvent("cms_link", label, url, cmsInfo);
  };
  if (!columns || columns.length === 0) {
    return null;
  }
  const limitedColumns = columns.slice(0, 4);
  const headingColor = transparent ? "text.onImpact.neutral-fixed" : "text.neutral";
  const descriptionColor = transparent ? "text.onImpact.neutral-fixed" : "text.subtle";
  return /* @__PURE__ */ jsx(Box, { className: columnsNavRootClassName, "data-testid": "columns-nav", hidden: !isVisible, style: !isVisible ? {
    display: "none"
  } : void 0, onMouseEnter, onMouseLeave, children: /* @__PURE__ */ jsx(Box, { className: columnsNavInnerClassName, style: {
    width: "100%",
    maxWidth: GROUP_WIDTH_MAP.xl,
    margin: "0 auto"
  }, children: limitedColumns.map((column, index) => {
    const isFirstColumn = index === 0;
    const isLastColumn = index === limitedColumns.length - 1;
    return /* @__PURE__ */ jsxs(React.Fragment, { children: [
      /* @__PURE__ */ jsxs(Box, { css: {
        flexDirection: "column",
        display: "flex",
        gap: "default",
        paddingBlock: "tighter",
        paddingInline: "default",
        xl: {
          flex: "1 1 0%",
          minWidth: "0"
        },
        xlDown: {
          width: "100%",
          paddingLeft: "default",
          paddingRight: "default",
          paddingTop: isFirstColumn ? "default" : "0",
          paddingBottom: isLastColumn ? "default" : "0"
        }
      }, children: [
        column.heading && /* @__PURE__ */ jsx(Text, { textStyle: "body-bold", asChild: true, css: {
          color: headingColor,
          margin: "0"
        }, children: /* @__PURE__ */ jsx("h3", { children: column.heading }) }),
        column.description && /* @__PURE__ */ jsx(Text, { textStyle: "body-sm", css: {
          color: descriptionColor
        }, asChild: true, children: /* @__PURE__ */ jsx("p", { children: column.description }) }),
        column.links && column.links.length > 0 && /* @__PURE__ */ jsx(List.Root, { appearance: "none", css: {
          display: "flex",
          flexDirection: "column",
          gap: "tighter",
          margin: "0",
          padding: "0"
        }, children: column.links.map((link, linkIndex) => /* @__PURE__ */ jsx(List.Item, { children: /* @__PURE__ */ jsx(Anchor, { href: link.url, target: link.target, rel: link.target === "_blank" ? "noopener noreferrer" : void 0, css: {
          textDecoration: "none",
          textStyle: "body-sm",
          fontWeight: "normal",
          color: "text.link.default",
          _hover: {
            textDecoration: "underline",
            color: "text.link.hover"
          },
          _visited: {
            color: "text.link.default"
          },
          xlDown: {
            display: "block",
            width: "100%",
            textAlign: "left",
            padding: "0"
          }
        }, onClick: yieldCallback(() => {
          handleLinkClick(link.title, link.url);
        }), children: link.title }) }, `${link.title}-${linkIndex}`)) })
      ] }),
      index < limitedColumns.length - 1 && /* @__PURE__ */ jsx(Box, { "aria-hidden": true, css: {
        display: "none",
        xl: {
          display: "block",
          width: "1px",
          flexShrink: 0,
          alignSelf: "stretch",
          backgroundColor: "border.muted"
        }
      } })
    ] }, index);
  }) }) });
};
export {
  ColumnsNav as default
};
